import type { Direction } from './types';

export const DIRS: readonly Direction[] = ['up', 'down', 'left', 'right'];

export function colOf(id: number, cols: number): number {
  return id % cols;
}
export function rowOf(id: number, cols: number): number {
  return Math.floor(id / cols);
}
export function idOf(row: number, col: number, cols: number): number {
  return row * cols + col;
}

/** Orthogonal neighbor id in a direction, or null at the grid edge. */
export function neighborId(id: number, dir: Direction, rows: number, cols: number): number | null {
  const r = rowOf(id, cols);
  const c = colOf(id, cols);
  let nr = r;
  let nc = c;
  if (dir === 'up') nr--;
  else if (dir === 'down') nr++;
  else if (dir === 'left') nc--;
  else nc++;
  if (nr < 0 || nc < 0 || nr >= rows || nc >= cols) return null;
  return idOf(nr, nc, cols);
}

export function manhattan(a: number, b: number, cols: number): number {
  return Math.abs(rowOf(a, cols) - rowOf(b, cols)) + Math.abs(colOf(a, cols) - colOf(b, cols));
}

/**
 * Direction of the first step along a shortest path from `from` to `to`,
 * over cells where `passable(id)` is true. null if unreachable or already there.
 */
export function bfsNextDir(
  from: number,
  to: number,
  rows: number,
  cols: number,
  passable: (id: number) => boolean,
): Direction | null {
  if (from === to) return null;
  const prev = new Map<number, { from: number; dir: Direction }>();
  const seen = new Set<number>([from]);
  const queue: number[] = [from];
  let found = false;
  while (queue.length > 0) {
    const cur = queue.shift() as number;
    if (cur === to) {
      found = true;
      break;
    }
    for (const dir of DIRS) {
      const n = neighborId(cur, dir, rows, cols);
      if (n === null || seen.has(n)) continue;
      // the destination is allowed even if not "passable" (e.g. target decoy to munch)
      if (n !== to && !passable(n)) continue;
      seen.add(n);
      prev.set(n, { from: cur, dir });
      queue.push(n);
    }
  }
  if (!found) return null;
  // walk back from `to` to the step taken out of `from`
  let cur = to;
  let firstDir: Direction | null = null;
  while (cur !== from) {
    const p = prev.get(cur);
    if (!p) return null;
    firstDir = p.dir;
    cur = p.from;
  }
  return firstDir;
}
